import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { Spinner } from '@gbv/ui';
import { useAuth } from './stores/auth';
import { ClientShell } from './shell/ClientShell';
import { LoginPage } from './pages/LoginPage';

// Each screen is its own chunk, so the charts on the dashboard and calls
// pages never land in the initial bundle.
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const ReportDetailPage = lazy(() => import('./pages/ReportDetailPage'));
const CallsPage = lazy(() => import('./pages/CallsPage'));
const AppointmentsPage = lazy(() => import('./pages/AppointmentsPage'));
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const RecordsPage = lazy(() => import('./pages/RecordsPage'));
const RecordDetailPage = lazy(() => import('./pages/RecordDetailPage'));
const SocialPage = lazy(() => import('./pages/SocialPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const FilesPage = lazy(() => import('./pages/FilesPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const DownloadsPage = lazy(() => import('./pages/DownloadsPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));

function FullPageSpinner() {
  return (
    <div className="grid min-h-screen place-items-center bg-auth-page text-ink-soft">
      <Spinner size="lg" />
    </div>
  );
}

function RequireAuth() {
  const status = useAuth((s) => s.status);
  const restore = useAuth((s) => s.restore);

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  if (status === 'unknown') return <FullPageSpinner />;
  if (status === 'anonymous') return <Navigate to="/login" replace />;

  return (
    <ClientShell>
      <Suspense fallback={<FullPageSpinner />}>
        <Outlet />
      </Suspense>
    </ClientShell>
  );
}

function RedirectIfAuthenticated() {
  const status = useAuth((s) => s.status);
  const restore = useAuth((s) => s.restore);

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  if (status === 'unknown') return <FullPageSpinner />;
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <LoginPage />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <RedirectIfAuthenticated /> },
  {
    path: '/',
    element: <RequireAuth />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'reports/:id', element: <ReportDetailPage /> },
      { path: 'calls', element: <CallsPage /> },
      { path: 'appointments', element: <AppointmentsPage /> },
      { path: 'pipeline', element: <PipelinePage /> },
      { path: 'records/:kind', element: <RecordsPage /> },
      { path: 'records/:kind/:id', element: <RecordDetailPage /> },
      { path: 'social', element: <SocialPage /> },
      { path: 'messages', element: <MessagesPage /> },
      { path: 'files', element: <FilesPage /> },
      { path: 'invoices', element: <InvoicesPage /> },
      { path: 'downloads', element: <DownloadsPage /> },
      { path: 'account', element: <AccountPage /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
